/**
 * RegressionParametersSection Component
 * Handles regression configuration: method, tolerance type, and values
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '@/shared/components/forms/inputs/InputField';
import Dropdown from '@/shared/components/forms/selectors/Dropdown';
import { Button } from '@/shared/components/buttons/button';
import { ToleranceType, RegressionType } from '../types/dataCloud.types';

interface RegressionParametersSectionProps {
  // Regression parameters
  regressionType: RegressionType;
  toleranceType: ToleranceType;
  toleranceCnt: string;
  toleranceDep: string;
  toleranceDepExtra: string;

  // Setters
  onRegressionTypeChange: (type: RegressionType) => void;
  onToleranceTypeChange: (type: ToleranceType) => void;
  onToleranceCntChange: (value: string) => void;
  onToleranceDepChange: (value: string) => void;
  onToleranceDepExtraChange: (value: string) => void;

  // Actions
  onSubmit: () => void;

  // State
  isProcessing: boolean;
  canSubmit: boolean;
}

const RegressionParametersSection: React.FC<RegressionParametersSectionProps> = ({
  regressionType,
  toleranceType,
  toleranceCnt,
  toleranceDep,
  toleranceDepExtra,
  onRegressionTypeChange,
  onToleranceTypeChange,
  onToleranceCntChange,
  onToleranceDepChange,
  onToleranceDepExtraChange,
  onSubmit,
  isProcessing,
  canSubmit
}) => {
  const { t } = useTranslation('dataCloud');

  return (
    <div
      className="mt-4 sm:mt-6 bg-card rounded-lg shadow-sm p-3 sm:p-4 border border-border"
      role="region"
      aria-labelledby="data-cloud-regression-params-heading"
    >
      <h3 id="data-cloud-regression-params-heading" className="text-sm sm:text-base font-semibold mb-4 text-foreground">
        {t('regression.parameters')}
      </h3>

      <div className="space-y-4">
        {/* Regression and Tolerance Type Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <Dropdown
            label={t('regression.method')}
            selectedValue={regressionType}
            onChange={value => onRegressionTypeChange(value as RegressionType)}
            options={[
              { value: 'lin', label: 'lin' },
              { value: 'poly', label: 'poly' }
            ]}
            placeholder={t('regression.selectMethod')}
          />
          <Dropdown
            label={t('tolerance.type')}
            selectedValue={toleranceType}
            onChange={value => onToleranceTypeChange(value as ToleranceType)}
            options={[
              { value: 'cnt', label: 'CNT' },
              { value: 'dep', label: 'DEP' }
            ]}
            placeholder={t('tolerance.selectType')}
          />
        </div>

        {/* Tolerance Value Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {toleranceType === 'cnt' && (
            <InputField
              label={t('tolerance.width')}
              value={toleranceCnt}
              onChange={onToleranceCntChange}
              min={0}
              max={1000000}
              data-testid="dataCloud-toleranceCnt"
            />
          )}
          {toleranceType === 'dep' && (
            <>
              <InputField
                label={t('tolerance.widthIndependent')}
                value={toleranceDep}
                onChange={onToleranceDepChange}
                min={0}
                max={1000000}
                data-testid="dataCloud-toleranceDep"
              />
              <InputField
                label={t('tolerance.widthExtra')}
                value={toleranceDepExtra}
                onChange={onToleranceDepExtraChange}
                min={0}
                max={1000000}
                data-testid="dataCloud-toleranceDepExtra"
              />
            </>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-2 sm:space-x-4 pt-2">
          <Button
            onClick={onSubmit}
            disabled={!canSubmit || isProcessing}
            className="w-auto text-xs sm:text-sm"
          >
            {isProcessing
              ? t('process.processing')
              : t('process.processData')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegressionParametersSection;
